/**
 * Export control — master spec §8. One gate at creation, one at upload.
 *
 * **The wording is load-bearing and must not be paraphrased**, so it lives
 * here, in the package both sides import, rather than being retyped in the
 * form. It asks about *data entry*, not about the program: "Is this
 * opportunity ITAR?" would be answered *yes* for most defense deals, refusing
 * most of the funnel and pushing those deals into spreadsheets — a worse
 * compliance outcome and a lost forecast.
 *
 * Folding **"or I'm not sure"** into the blocking answer is the other half of
 * it; otherwise the safe answer for an uncertain rep is to guess "No".
 */

import { z } from 'zod';
import { dataEnvelope } from './http.js';

export const EXPORT_GATE_HEADING = 'Export control';

export const EXPORT_GATE_QUESTION =
  'Will any export-controlled technical data — controlled specifications, drawings, test ' +
  'results, or measured performance data — be entered into this system for this opportunity?';

export const EXPORT_GATE_ANSWER_NO = 'No — commercial information only';
export const EXPORT_GATE_ANSWER_YES = "Yes, or I'm not sure";

export const EXPORT_GATE_HELP =
  'This system is not authorized to hold export-controlled technical data. Commercial deal ' +
  'information for controlled programs is fine.';

/** The one-line form of the same question, shown on every upload (§8 item 7). */
export const ATTACHMENT_ATTESTATION_QUESTION =
  'Does this file contain export-controlled technical data — controlled specifications, ' +
  'drawings, test results, or measured performance data?';

/**
 * The attestation as it crosses the wire.
 *
 * `false` is "No — commercial information only" and proceeds.
 * `true` is "Yes, or I'm not sure" and is refused with `403 EXPORT_BLOCKED`.
 *
 * It is REQUIRED, never defaulted: a default is a click-through, and §8 item 1
 * is explicit that a client-side check is theatre — one API call around it and
 * the record exists.
 */
export const exportAttestationSchema = z.object({
  exportControlledDataWillBeEntered: z.boolean(),
});

export type ExportAttestation = z.infer<typeof exportAttestationSchema>;

/**
 * §8 item 3: a one-time per-user policy acknowledgment, enforced by MIDDLEWARE
 * on every route except the three exemptions. It is the artifact compliance
 * will actually ask for.
 */
export const acknowledgeExportPolicySchema = z.object({
  policyVersion: z.string().min(1).max(64),
});

export const exportPolicyStatusSchema = z.object({
  policyVersion: z.string(),
  acknowledged: z.boolean(),
  acknowledgedAt: z.string().nullable(),
  /**
   * The Admin-configured refusal text from `app_settings`, carried on
   * `GET /api/me` because Phase 4's **blocking** acknowledgment screen has
   * nowhere else to read it from: the §8 item 3 middleware refuses every route
   * but health, `/api/me` and the acknowledgment endpoint, so `/api/reference`
   * is unreachable until the user has already acknowledged. Never hardcoded in
   * the client (§8).
   */
  refusalText: z.string().optional(),
});

export type ExportPolicyStatus = z.infer<typeof exportPolicyStatusSchema>;
export const exportPolicyStatusResponseSchema = dataEnvelope(exportPolicyStatusSchema);

/**
 * §8 item 4: the pattern classes the save-path scan reports.
 *
 * The event names the field and the CLASS. **Never the matched text** — and
 * never the content that triggered it, in `export_events`, in `audit_log`, or
 * in any log sink.
 */
export const PATTERN_CLASSES = [
  'itar_ear_marking',
  'classification_marking',
  'export_control_reference',
  'drawing_reference',
  'measured_rf_performance',
  'military_specification',
  'contract_vehicle',
] as const;

export type PatternClass = (typeof PATTERN_CLASSES)[number];
export const patternClassSchema = z.enum(PATTERN_CLASSES);

/**
 * What a write returns when the scan matched. It **warns and never blocks** —
 * a false-positive blocker trains people to route around the system.
 */
export const patternWarningSchema = z.object({
  field: z.string(),
  patternClass: patternClassSchema,
});

export type PatternWarning = z.infer<typeof patternWarningSchema>;

/**
 * The fields every mutation response carries beside its record.
 *
 * `patternWarnings` is §8 item 4's non-blocking result; `concurrencyNotice` is
 * §6.10's last-write-wins toast. Both are optional and neither is an error, so
 * they ride in the `data` envelope rather than the `error` one.
 */
export const patternWarningEnvelopeFields = {
  patternWarnings: z.array(patternWarningSchema).optional(),
  concurrencyNotice: z
    .object({
      changedBy: z.string(),
      changedByUserId: z.string().uuid(),
      changedAt: z.string(),
      fields: z.array(z.string()),
    })
    .nullable()
    .optional(),
};
